import React, { memo } from 'react'
import { useWatch } from 'react-hook-form'
import capitalize from 'lodash/capitalize'
import moment from 'moment'
import ControlledDatePicker from 'transfix-form/components/ControlledDatePicker'
import ControlledTimeInput from 'transfix-form/components/ControlledTimeInput'
import { FormHelperText } from 'transfix-ui/components/FormHelperText'
import { TIME_INPUT_FORMAT } from 'transfix-ui/components/TimeInput'
import Grid from 'transfix-ui/core/Grid'
import { MaterialUiPickersDate } from 'transfix-ui/core/pickers'
import { useCreateShipmentFormContext } from 'apps/marketplace/pages/create-shipment/context'
import {
  getStopErrorMessage,
  getShipmentStopFieldError,
  getShipmentStopErrorMessage,
} from 'apps/marketplace/pages/create-shipment/utils'
import FORMATS from 'constants/datetime'
import { IStopFormSectionProps } from '../StopFormSection'

/**
 * @example getFormattedDate(date) // '2022-10-22'
 */
const getFormattedDate = (date: MaterialUiPickersDate) => moment(date).format(FORMATS.yearFirst)

type TDateKey = 'startAtDate' | 'endAtDate'
type TTimeKey = 'startAtTime' | 'endAtTime'

export interface IControlledDateTimeInputProps extends Pick<IStopFormSectionProps, 'stopIndex'> {
  dateType: 'start' | 'end'
}

const ControlledDateTimeInput = memo<IControlledDateTimeInputProps>(({ dateType, stopIndex }) => {
  const parentKey = `shipment.stops[${stopIndex}]`
  const dateKey: TDateKey = `${dateType}AtDate`
  const timeKey: TTimeKey = `${dateType}AtTime`

  const dateTypeTitle = capitalize(dateType)

  /**
   * Form Context
   */
  const { control, errors } = useCreateShipmentFormContext()

  /**
   * Confirmed
   */
  const confirmed: boolean = useWatch({
    control,
    name: `${parentKey}.confirmed`,
    defaultValue: false,
  })
  const isStartTimeRequired = dateType === 'start' && confirmed

  /**
   * Errors
   */
  const { shipment: shipmentErrors } = errors || {}
  const stopErrors = getShipmentStopFieldError(shipmentErrors, stopIndex)
  const stopErrorMessage = getShipmentStopErrorMessage(shipmentErrors, stopIndex)

  return (
    <>
      <Grid item xs={12} xl={6}>
        <ControlledDatePicker
          control={control}
          name={`${parentKey}.${dateKey}`}
          defaultValue={null}
          transform={getFormattedDate}
          DatePickerProps={{
            label: `${dateTypeTitle} Date`,
            dataTest: `${dateType}-at-date-field`,
            placeholder: 'MM/DD/YYYY',
            error: !!getStopErrorMessage(stopErrors, dateKey),
            validationMessage: getStopErrorMessage(stopErrors, dateKey),
            required: true,
          }}
        />
      </Grid>
      <Grid item xs={12} xl={6}>
        <ControlledTimeInput
          control={control}
          name={`${parentKey}.${timeKey}`}
          TimeInputProps={{
            label: `${dateTypeTitle} Time`,
            dataTest: `${dateType}-at-time-field`,
            timeFormat: TIME_INPUT_FORMAT.MILITARY,
            placeholder: 'HH:MM',
            invalid: !!getStopErrorMessage(stopErrors, timeKey),
            validationMessage: getStopErrorMessage(stopErrors, timeKey),
            required: isStartTimeRequired,
          }}
        />
      </Grid>
      {stopErrorMessage && (
        <Grid item xs={12}>
          <FormHelperText className='Mui-error'>{stopErrorMessage}</FormHelperText>
        </Grid>
      )}
    </>
  )
})

export default ControlledDateTimeInput
